import type { MaterialUnit } from 'api/models/mainservice';
import type { StorePermission } from 'api/models/unified';
import type { TableQuery, TableResult } from 'common/models';

export const StorePermissionArray: StorePermission[] = ['ReadAndUse', 'Read', 'Manage'];

export const MaterialUnitArray: MaterialUnit[] = [
  'Amounts',
  'Grams',
  'KiloGrams',
  'CentiMeters',
  'Meters',
  'LinearMeters',
  'SquareMeters',
  'CubicMeters',
  'MilliLiters',
  'Liters',
  'Packages',
  'Tons',
  'Sets'
];

export interface Material {
  name: string;
  identificationNumber?: string | null;
  description?: string | null;
  supplierCode?: string | null;
  externalId?: string | null;
  /** @format double */
  price?: number | null;
  /** @format double */
  costPrice?: number | null;
  unit: MaterialUnit;
  storeAmounts: MaterialStoreAmount[];
  /** @format uuid */
  categoryId: string;
  categoryName: string;
  /** @format uuid */
  supplierId: string;
  supplierName: string;
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
}

export interface MaterialStoreAmount {
  /** @format uuid */
  storeId: string;
  storeName: string;
  /** @format double */
  amount?: number | null;
}

export interface MaterialCategory {
  /** @format uuid */
  id: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  name: string;
  slug?: string | null;
  /** @format int32 */
  sortOrder: number;
}

export interface Store {
  name: string;
  externalId?: string | null;
  availableFor: Record<string, string | null>;

  /** @format uuid */
  id: string;

  /** @format date-time */
  created: string;

  /** @format date-time */
  modified: string;
}

export interface StoreDetail {
  material: TableResult<MaterialAtStore>;
  name: string;
  externalId?: string | null;
  availableFor: Record<string, string | null>;

  /** @format uuid */
  id: string;

  /** @format date-time */
  created: string;

  /** @format date-time */
  modified: string;
}

export interface MaterialToAdd {
  /** @format uuid */
  id?: string;
  name: string;
  identificationNumber?: string | null;
  description?: string | null;
  supplierCode?: string | null;
  externalId?: string | null;
  /** @format uuid */
  categoryId: string;
  /** @format uuid */
  supplierId: string;
  unit: MaterialUnit;
  /** @format double */
  price: number;
  /** @format double */
  costPrice?: number | null;
  storeAmounts: MaterialToAddToStore[];
}

export interface MaterialToAddToStore {
  /** @format uuid */
  storeId: string;
  /** @format double */
  amount?: number | null;
}

export interface MaterialToEdit {
  name: string;
  identificationNumber?: string | null;
  description?: string | null;
  supplierCode?: string | null;
  externalId?: string | null;
  /** @format uuid */
  categoryId: string;
  /** @format uuid */
  supplierId: string;
  unit: MaterialUnit;
  /** @format double */
  price: number;
  /** @format double */
  costPrice?: number | null;
}

export interface StoreToAdd {
  name: string;
  availableFor: string[];
  externalId?: string | null;
}

export interface MaterialAtStore {
  /** @format uuid */
  id: string;
  /** @format uuid */
  materialId: string;
  /** @format date-time */
  created: string;
  /** @format date-time */
  modified: string;
  name: string;
  identificationNumber?: string | null;
  description?: string | null;
  supplierCode?: string | null;
  materialUnit: MaterialUnit;
  /** @format double */
  price?: number | null;
  /** @format double */
  costPrice?: number | null;
  /** @format double */
  amount?: number | null;
  /** @format uuid */
  materialCategoryId: string;
  /** @format uuid */
  materialSupplierId: string;
}

export interface IncomeMaterial {
  materialId: string;
  description?: string | null;
  name: string;
  price?: number | null;
  amount?: number | null;
}

export interface OutcomeMaterial extends IncomeMaterial {
  maximumAmount?: number;
}

export interface MaterialToStore {
  materialId: string;
  price?: number | null;
  amount?: number | null;
}

export interface MaterialToMove {
  materialId: string;
  amount?: number | null;
}

export const MATERIAL_SORT_KEYS = [
  'name',
  'categoryName',
  'price',
  'costPrice',
  'identificationNumber',
  'supplierCode',
  'supplierName',
  'externalId'
] as const;

export const PRICE_LIST_MATERIAL_SORT_KEYS = [
  ...MATERIAL_SORT_KEYS,
  'modified',
  'priceListPrice'
] as const;

export type MaterialSortBy = (typeof PRICE_LIST_MATERIAL_SORT_KEYS)[number];

export const STORE_MATERIAL_SORT_KEYS = [
  'name',
  'materialCategoryName',
  'price',
  'costPrice',
  'amount',
  'identificationNumber',
  'supplierCode',
  'materialSupplierName',
  'externalId'
] as const;

export type StoreMaterialSortBy = (typeof STORE_MATERIAL_SORT_KEYS)[number];

export interface MaterialTableQuery extends TableQuery<MaterialSortBy> {
  searchPhrase?: string;
  supplierId?: string | null;
  categoryId?: string | null;
  isStocked?: boolean;
  excludePriceListId?: string | null;
}

export interface StoreMaterialTableQuery extends TableQuery<StoreMaterialSortBy> {
  searchPhrase?: string;
  materialSupplierId?: string | null;
  materialCategoryId?: string | null;
}

export interface ExistingMaterialQuery {
  identificationNumber?: string | null;
  excludeMaterialId?: string | null;
}

export interface ExistingMaterial {
  /** @format uuid */
  id: string;
  name: string;
  identificationNumber?: string | null;
}

export interface SelectedMaterialItem {
  materialId: string;
  name: string;
  unit: MaterialUnit;
  amount?: number | null;
  maxAmount?: number | null;
}

export interface MaterialSelectConfig {
  material: MaterialSelectItem[];
  isAllSelected: boolean;
  lessThanPageSizeSelected: boolean;
}

export interface MaterialSelectItem {
  id: string;
  price?: number | null;
  priceListPrice?: number;
  name: string;
}
